import { STANDARD_STAGE_BOX_MODELS } from "./patch";
import { CONSOLE_MODELS } from "./system";
/**
 * Which stage boxes belong to which console - the table «console -> stage boxes» (SCRUM-866).
 *
 * Owner, 27.10.2026: «В райдере запрашивается микшерная система (или микшерные системы), прокатчик предлагает
 * подходящий стейджбокс или подтверждает конкретный если запрошен. Это должно быть у нас в каталоге по любому,
 * это не стулья.», «нам надо собрать все стейджбоксы для всех пультов в каталог».
 *
 * ONE SOURCE OF TRUTH, next to `system.ts`. A box is named by its CATALOGUE KEY - the `key` of a row in
 * `apps/api/import/catalog-additions/*.json` (category `stage-boxes`), which `ids.json` turns into `equipment.id`
 * - so the offer can resolve a suggested box to a real catalogue item. Three things are kept apart:
 *
 *   - `ConsoleModel.stageBoxIds` (`system.ts`): the boxes SUGGESTED for the desk, by preset id, in S1's order.
 *     NOT touched here: `mixing-system.ts` and its pinned tests read it. Each one resolves, through the preset's
 *     `catalogKey`, to a row of THIS table (`consoleNativeStageBoxKeys`).
 *   - this table: every box the desk's maker (or its box's maker) publishes as working with it - a superset.
 *     Everything the offer may CONFIRM when a rider names a box, and everything the warehouse may be asked for.
 *   - a desk absent from the table: nothing is claimed. Analogue desks have a multicore; modular-I/O desks
 *     (RIVAGE, Quantum 5/7) are listed because the maker lists their racks, but `stageBoxIds` stays `[]` (D19).
 *
 * NOTHING IS WRITTEN FROM MEMORY. Every group below names the pages it was read from (opened 27.10.2026); the
 * same pages are tabulated in `docs/research/STAGE_BOXES_BY_CONSOLE.md`. Soundcraft: its pages answer 403 to plain
 * curl; they were read from copies saved by a browser on 27.10.2026 (the owner allowed it). Mackie's DL32S / DL16S ARE the stage box (a desk with
 * its own preamps), so they have no separate row.
 *
 * A group's console may end in `*` - a prefix, so a variant row added later (`digico-quantum-338-pulse`) is
 * covered without a second edit.
 */
/** Native protocols of the boxes, as the makers' own pages name them. A closed list; APPEND-ONLY. */
export const STAGE_BOX_PROTOCOLS = [
    "AES50",
    "Dante",
    "MADI",
    "Optocore",
    "TWINLANe",
    "EtherSound",
    "StageConnect",
    "dSNAKE",
    "DX link",
    "gigaACE",
    "REAC",
    "AVB",
    "MegaCOMMS",
    "AES67",
];
/**
 * The protocol(s) each catalogue box speaks. `[]` = the page names none that fits the list (a Cat5e link with no
 * name, an Avid rack whose published sheet is mechanical only): unknown is not guessed.
 */
export const STAGE_BOX_PROTOCOLS_BY_KEY = {
    // Yamaha: the R / T series are Dante racks, the RPio racks speak TWINLANe, the SB168-ES speaks EtherSound.
    "yamaha-rio3224-d": ["Dante"],
    "yamaha-rio1608-d": ["Dante"],
    "yamaha-ri8-d": ["Dante"],
    "yamaha-ro8-d": ["Dante"],
    "yamaha-rio3224-d2": ["Dante"],
    "yamaha-rio1608-d2": ["Dante"],
    "yamaha-rio3224-d3": ["Dante"],
    "yamaha-rio1608-d3": ["Dante"],
    "yamaha-tio1608-d": ["Dante"],
    "yamaha-tio1608-d2": ["Dante"],
    "yamaha-rpio622": ["TWINLANe"],
    "yamaha-rpio222": ["TWINLANe"],
    "yamaha-sb168-es": ["EtherSound"],
    // DiGiCo
    "digico-sd-rack": ["Optocore", "MADI"],
    "digico-sd-mini-rack": ["MADI"],
    "digico-sd-nano-rack": ["MADI"],
    "digico-d-rack": [],
    "digico-d2-rack": ["MADI"],
    "digico-mq-rack": ["MADI"],
    "digico-dq-rack": ["Dante"],
    // Midas / Behringer: the AES50 family and the StageConnect boxes
    "midas-dl151": ["AES50"],
    "midas-dl154": ["AES50"],
    "midas-dl155": ["AES50"],
    "midas-dl251": ["AES50"],
    "midas-dl252": ["AES50"],
    "midas-dl231": ["AES50"],
    "midas-dl431": ["AES50"],
    "midas-dl451": ["AES50"],
    "midas-dl16": ["AES50"],
    "midas-dl32": ["AES50"],
    "midas-dl8": ["AES50"],
    "behringer-s16": ["AES50"],
    "behringer-s32": ["AES50"],
    "behringer-sd8": ["AES50"],
    "behringer-sd16": ["AES50"],
    "behringer-sc88": ["StageConnect"],
    "behringer-sc16-i": ["StageConnect"],
    "behringer-sc16-o": ["StageConnect"],
    // Allen & Heath
    "allen-heath-ar2412": ["dSNAKE"],
    "allen-heath-ab168": ["dSNAKE"],
    "allen-heath-ar84": ["dSNAKE"],
    "allen-heath-dx168": ["DX link"],
    "allen-heath-dx164-w": ["DX link"],
    "allen-heath-dt164-w": ["Dante"],
    "allen-heath-gx4816": ["gigaACE"],
    "allen-heath-dlive-dm32": ["gigaACE"],
    "allen-heath-dlive-dm48": ["gigaACE"],
    "allen-heath-dlive-dm64": ["gigaACE"],
    "allen-heath-dlive-dm32u": ["gigaACE"],
    "allen-heath-dlive-dm48u": ["gigaACE"],
    "allen-heath-dlive-dm64u": ["gigaACE"],
    "allen-heath-dlive-cdm32u": ["gigaACE"],
    "allen-heath-dlive-cdm48u": ["gigaACE"],
    "allen-heath-dlive-cdm64u": ["gigaACE"],
    // Avid: the S6L racks; the published sheet is mechanical, no protocol is named
    "avid-venue-stage-64": [],
    "avid-venue-stage-48": [],
    "avid-venue-stage-32": [],
    "avid-venue-stage-16": [],
    "avid-venue-local-16": [],
    // SSL
    "ssl-sb-32-24": ["Dante"],
    "ssl-sb-16-12": ["Dante"],
    "ssl-sb-i16": ["Dante"],
    "ssl-sb-8-8": ["Dante"],
    "ssl-ml-32-32": ["MADI"],
    // Roland
    "roland-s-0808": ["REAC"],
    "roland-s-0816": ["REAC"],
    "roland-s-1608": ["REAC"],
    "roland-s-2416": ["REAC"],
    "roland-s-4000s": ["REAC"],
    // Soundcraft: Cat5 or optical MADI, through a MADI card in the console's option slot (the Mini 32i/16i have MADI on the chassis)
    "soundcraft-vi-stagebox": ["MADI"],
    "soundcraft-mini-stagebox-32i": ["MADI"],
    "soundcraft-mini-stagebox-16i": ["MADI"],
    "soundcraft-mini-stagebox-32r": ["MADI"],
    "soundcraft-mini-stagebox-16r": ["MADI"],
    "soundcraft-compact-stagebox": ["MADI"],
    // PreSonus
    "presonus-nsb-16-8": ["AVB"],
    "presonus-nsb-32-16": ["AVB"],
    // Cadac, Lawo, Studer
    "cadac-cdc-io-6448": ["MegaCOMMS"],
    "cadac-cdc-io-3216": ["MegaCOMMS"],
    "lawo-a-stage-48": ["AES67", "MADI"],
    "lawo-a-stage-64": ["AES67", "MADI"],
    "lawo-a-stage-80": ["AES67", "MADI"],
    "studer-compact-stagebox": ["MADI"],
    "studer-d21m": ["MADI"],
};
/** The AES50 boxes of Midas / Behringer: every one is a Cat5e snake on the AES50 ports of the X32 / M32 / WING. */
const AES50_BOXES = ["behringer-s16", "midas-dl32", "behringer-s32", "behringer-sd8", "behringer-sd16", "midas-dl16", "midas-dl8"];
const AES50_SOURCES = [
    "https://www.behringer.com/en/products/0603-ACE",
    "https://www.behringer.com/en/products/0603-AEV",
    "https://www.midasconsoles.com/en/products/0603-aeo",
    "https://www.midasconsoles.com/en/products/0606-ACR",
    "https://www.behringer.com/en/products/0606-ACQ",
    "https://www.behringer.com/en/products/0606-ACO",
    "https://www.behringer.com/en/products/0606-ACP",
];
const AH_DSNAKE_SOURCES = [
    "https://www.allen-heath.com/content/uploads/2023/06/AR2412-Technical-Datasheet-1.pdf",
    "https://www.allen-heath.com/content/uploads/2023/06/AB168-Technical-Datasheet.pdf",
    "https://www.allen-heath.com/content/uploads/2023/06/AR84-Technical-Datasheet.pdf",
];
const YAMAHA_R_SERIES = "https://usa.yamaha.com/products/proaudio/interfaces/r_series_adda/index.html";
const DIGICO_RACKS = "https://digico.biz/racks-and-i-os/";
export const CONSOLE_STAGE_BOX_GROUPS = [
    // --- DiGiCo. D-Rack: «available as an option on all SD-Series consoles»; MQ-Rack: «any two SD Range or Quantum
    // consoles»; DQ-Rack: «connection to a DiGiCo console via the DMI-DANTE64@96». S21 / S31 take DMI cards, not racks.
    {
        consoles: ["digico-sd7", "digico-sd5", "digico-sd10", "digico-sd12", "digico-sd12-96", "digico-sd9", "digico-sd8", "digico-sd11"],
        boxes: ["digico-d2-rack", "digico-sd-rack", "digico-d-rack", "digico-sd-mini-rack", "digico-sd-nano-rack", "digico-mq-rack", "digico-dq-rack"],
        sources: [DIGICO_RACKS, "https://digico.biz/racks/d-rack/", "https://digico.biz/racks/mq-rack/", "https://digico.biz/racks/dq-rack/", "https://digico.biz/racks/sd-rack/", "https://digico.biz/racks/sd-mini-rack/"],
    },
    {
        consoles: ["digico-quantum-*"],
        boxes: ["digico-d2-rack", "digico-sd-rack", "digico-mq-rack", "digico-dq-rack"],
        sources: [DIGICO_RACKS, "https://digico.biz/racks/mq-rack/", "https://digico.biz/racks/dq-rack/"],
    },
    // --- Yamaha
    {
        consoles: ["yamaha-rivage-*"],
        boxes: ["yamaha-rpio622", "yamaha-rpio222", "yamaha-rio3224-d3", "yamaha-rio1608-d3", "yamaha-rio3224-d2", "yamaha-rio1608-d2"],
        sources: ["https://usa.yamaha.com/products/proaudio/mixers/rivage_pm/components.html", "https://usa.yamaha.com/products/proaudio/interfaces/r_series_adda_3/specs.html", "https://usa.yamaha.com/products/proaudio/interfaces/r_series_adda_2/specs.html"],
    },
    {
        consoles: ["yamaha-dm7", "yamaha-dm7-compact"],
        boxes: ["yamaha-rio1608-d3", "yamaha-rio3224-d3"],
        sources: ["https://usa.yamaha.com/products/proaudio/interfaces/r_series_adda_3/specs.html"],
    },
    {
        consoles: ["yamaha-cl5", "yamaha-cl3", "yamaha-cl1", "yamaha-ql5", "yamaha-ql1"],
        boxes: ["yamaha-rio1608-d2", "yamaha-rio3224-d2", "yamaha-rio1608-d3", "yamaha-rio3224-d3", "yamaha-rio1608-d", "yamaha-rio3224-d", "yamaha-ri8-d", "yamaha-ro8-d"],
        sources: ["https://usa.yamaha.com/products/proaudio/mixers/cl_series/index.html", "https://usa.yamaha.com/products/proaudio/mixers/ql_series/index.html", YAMAHA_R_SERIES],
    },
    {
        consoles: ["yamaha-tf5", "yamaha-tf3", "yamaha-tf1"],
        boxes: ["yamaha-tio1608-d"],
        sources: ["https://usa.yamaha.com/products/proaudio/mixers/tf/index.html"],
    },
    {
        consoles: ["yamaha-dm3", "yamaha-dm3-d"],
        boxes: ["yamaha-tio1608-d2", "yamaha-tio1608-d"],
        sources: ["https://usa.yamaha.com/products/proaudio/mixers/dm3/index.html"],
    },
    {
        consoles: ["yamaha-pm5d", "yamaha-pm5d-rh", "yamaha-m7cl-48", "yamaha-m7cl-32", "yamaha-ls9-32", "yamaha-ls9-16"],
        boxes: ["yamaha-sb168-es"],
        sources: ["https://usa.yamaha.com/products/proaudio/interfaces/sb168-es/index.html"],
    },
    // --- Midas. DL251: «ideal partner to the Midas M Series and PRO Series»; DL431: «PRO3, PRO6, PRO9, XL8 and PRO X».
    {
        consoles: ["midas-hd96-24"],
        boxes: ["midas-dl251", "midas-dl231", "midas-dl252", "midas-dl451"],
        sources: ["https://www.midasconsoles.com/en/products/0606-ABT", "https://midasconsoles.com/en/case-study/francis-cabrel-trobador-tour"],
    },
    {
        consoles: ["midas-pro-x", "midas-pro9", "midas-pro6", "midas-pro3"],
        boxes: ["midas-dl251", "midas-dl252", "midas-dl431", "midas-dl151", "midas-dl154", "midas-dl155"],
        sources: ["https://www.midasconsoles.com/en/products/0606-AAT", "https://www.midasconsoles.com/en/products/0606-AAU", "https://www.midasconsoles.com/en/products/0606-ABR"],
    },
    {
        consoles: ["midas-pro2", "midas-pro2c"],
        boxes: ["midas-dl251", "midas-dl151", "midas-dl154", "midas-dl155"],
        sources: ["https://www.midasconsoles.com/en/products/0606-AAU", "https://www.midasconsoles.com/en/products/0606-ABR"],
    },
    {
        consoles: ["midas-xl8"],
        boxes: ["midas-dl431"],
        sources: ["https://www.midasconsoles.com/en/products/0606-AAT"],
    },
    {
        consoles: ["midas-m32-live", "midas-m32r", "midas-m32c"],
        boxes: [...AES50_BOXES, "midas-dl251"],
        sources: [...AES50_SOURCES, "https://www.midasconsoles.com/en/products/0606-AAU"],
    },
    // --- Behringer: X32 (S16 is the box the maker's page names) and WING (AES50 plus StageConnect)
    {
        consoles: ["behringer-x32", "behringer-x32-compact", "behringer-x32-producer", "behringer-x32-rack"],
        boxes: AES50_BOXES,
        sources: AES50_SOURCES,
    },
    {
        consoles: ["behringer-wing", "behringer-wing-compact", "behringer-wing-rack"],
        boxes: [...AES50_BOXES, "behringer-sc88", "behringer-sc16-i", "behringer-sc16-o"],
        sources: [...AES50_SOURCES, "https://www.behringer.com/en/products/0606-AEF", "https://www.behringer.com/en/products/0606-AEE", "https://www.behringer.com/en/products/0606-AED"],
    },
    // --- Allen & Heath. The dSNAKE racks: «Compatible with SQ, Qu, Avantis, AHM-64, GLD». DX168 / DX164-W / DT164-W:
    // «Compatible with dLive, Avantis, SQ and AHM-64». GX4816: Avantis and SQ.
    {
        consoles: ["allen-heath-dlive"],
        boxes: [
            "allen-heath-dx168",
            "allen-heath-dx164-w",
            "allen-heath-dt164-w",
            "allen-heath-dlive-dm32",
            "allen-heath-dlive-dm48",
            "allen-heath-dlive-dm64",
            "allen-heath-dlive-dm32u",
            "allen-heath-dlive-dm48u",
            "allen-heath-dlive-dm64u",
            "allen-heath-dlive-cdm32u",
            "allen-heath-dlive-cdm48u",
            "allen-heath-dlive-cdm64u",
        ],
        sources: ["https://www.allen-heath.com/content/uploads/2023/06/DX168-Datasheet.pdf", "https://www.allen-heath.com/content/uploads/2023/06/DX164-W-Datasheet.pdf", "https://www.allen-heath.com/content/uploads/2023/07/DM32-Datasheet-2.pdf", "https://www.allen-heath.com/content/uploads/2025/03/CDM48U-Datasheet.pdf"],
    },
    {
        consoles: ["allen-heath-avantis", "allen-heath-sq-5", "allen-heath-sq-6", "allen-heath-sq-7"],
        boxes: ["allen-heath-dx168", "allen-heath-gx4816", "allen-heath-dx164-w", "allen-heath-dt164-w", ...["allen-heath-ar2412", "allen-heath-ab168", "allen-heath-ar84"]],
        sources: ["https://www.allen-heath.com/content/uploads/2023/06/DX168-Datasheet.pdf", "https://www.allen-heath.com/content/uploads/2023/06/GX4816-Datasheet.pdf", "https://www.allen-heath.com/content/uploads/2023/06/DX164-W-Datasheet.pdf", ...AH_DSNAKE_SOURCES],
    },
    {
        consoles: ["allen-heath-qu-32", "allen-heath-qu-24", "allen-heath-qu-16"],
        boxes: ["allen-heath-ab168", "allen-heath-ar2412", "allen-heath-ar84"],
        sources: AH_DSNAKE_SOURCES,
    },
    // --- Avid. The S6L racks are the S6L system's own (S6L_Mechanical_Specs); the S3L-X takes the Stage 16.
    {
        consoles: ["avid-venue-s6l-*"],
        boxes: ["avid-venue-stage-16", "avid-venue-stage-32", "avid-venue-stage-48", "avid-venue-stage-64", "avid-venue-local-16"],
        sources: ["https://www.avid.com/products/venue-s6l-system/build-your-own", "https://resources.avid.com/SupportFiles/VENUE/S6L_Mechanical_Specs.pdf"],
    },
    // S3L-X: the Stage 16 is S1's own pairing (system.ts, S-grade source); the retailer page answers 403 to us.
    {
        consoles: ["avid-venue-s3l-x"],
        boxes: ["avid-venue-stage-16"],
        sources: ["https://www.sweetwater.com/insync/avid-s3l-system/"],
    },
    // --- SSL: Dante boxes and the MADI box are listed under «Live Sound» on the maker's site
    {
        consoles: ["ssl-live-*"],
        boxes: ["ssl-sb-32-24", "ssl-sb-16-12", "ssl-sb-i16", "ssl-sb-8-8", "ssl-ml-32-32"],
        sources: ["https://solidstatelogic.com/products/dante-live-sound", "https://solidstatelogic.com/products/madi"],
    },
    // --- Roland: V-Mixers and the M-5000 are REAC desks; every S-series snake is a REAC head
    {
        consoles: ["roland-m-*"],
        boxes: ["roland-s-1608", "roland-s-2416", "roland-s-0808", "roland-s-0816", "roland-s-4000s"],
        sources: ["https://proav.roland.com/global/products/s-1608/", "https://proav.roland.com/global/products/s-2416/", "https://proav.roland.com/global/solutions/v-mixing_system/digital_snake_system_overview/s-4000_overview/", "https://proav.roland.com/global/products/m-380/", "https://proav.roland.com/global/ohrca/options.html"],
    },
    // --- Soundcraft (pages saved 27.10.2026, read by the coordinator's browser; the site answers 403 to plain curl).
    // Compatibility lists on the Vi Stagebox / Mini 32R/16R / Compact pages: Vi1, Vi2, Vi4, Vi6, Vi1000-Vi7000, Si Impact,
    // Si Performer, Si Expression, Si Compact. The Mini 32i/16i page speaks of Si consoles only. Ui12/16/24R have no stagebox
    // (the Ui24R «can double as a stagebox» itself). Si Performer, Vi1000 and Ui are not console rows yet.
    {
        consoles: ["soundcraft-vi7000", "soundcraft-vi5000", "soundcraft-vi3000", "soundcraft-vi2000", "soundcraft-vi6", "soundcraft-vi4", "soundcraft-vi1"],
        boxes: ["soundcraft-vi-stagebox", "soundcraft-mini-stagebox-32r", "soundcraft-mini-stagebox-16r", "soundcraft-compact-stagebox"],
        sources: [
            "https://www.soundcraft.com/en-US/products/vi-stagebox",
            "https://www.soundcraft.com/en-US/products/mini-stagebox-32r-16r",
            "https://www.soundcraft.com/en-US/products/compact-stagebox",
        ],
    },
    {
        consoles: ["soundcraft-si-expression-1", "soundcraft-si-expression-2", "soundcraft-si-expression-3", "soundcraft-si-impact"],
        boxes: ["soundcraft-mini-stagebox-32i", "soundcraft-mini-stagebox-16i", "soundcraft-mini-stagebox-32r", "soundcraft-mini-stagebox-16r", "soundcraft-compact-stagebox", "soundcraft-vi-stagebox"],
        sources: [
            "https://www.soundcraft.com/en-US/products/mini-stagebox-32i-16i",
            "https://www.soundcraft.com/en-US/products/mini-stagebox-32r-16r",
            "https://www.soundcraft.com/en-US/products/compact-stagebox",
            "https://www.soundcraft.com/en-US/products/vi-stagebox",
            "https://www.soundcraft.com/en-US/products/si-impact",
        ],
    },
    // --- PreSonus: «Designed to work seamlessly with PreSonus StudioLive Series III mixers»
    {
        consoles: ["presonus-studiolive-*"],
        boxes: ["presonus-nsb-16-8", "presonus-nsb-32-16"],
        sources: ["https://www.presonus.com/products/nsb-168-networked-stage-box", "https://www.presonus.com/products/nsb-3216-networked-stage-box"],
    },
    // --- Broadcast desks
    {
        consoles: ["cadac-cdc-*"],
        boxes: ["cadac-cdc-io-3216", "cadac-cdc-io-6448"],
        sources: ["https://www.cadac-consoles.com/legacy-products"],
    },
    {
        consoles: ["lawo-mc2-56"],
        boxes: ["lawo-a-stage-48", "lawo-a-stage-64", "lawo-a-stage-80"],
        sources: ["https://lawo.com/products/a__line/", "https://lawo.com/mobile-recording-truck-with-lawo-mc%C2%B256/"],
    },
    {
        consoles: ["studer-vista-*"],
        boxes: ["studer-compact-stagebox", "studer-d21m"],
        sources: ["https://evertz.com/products/Compact_Stagebox", "https://evertz.com/products/D21m"],
    },
];
function expand(pattern) {
    if (pattern.endsWith("*")) {
        const prefix = pattern.slice(0, -1);
        return CONSOLE_MODELS.filter((c) => c.id.startsWith(prefix)).map((c) => c.id);
    }
    return CONSOLE_MODELS.some((c) => c.id === pattern) ? [pattern] : [];
}
function buildTable() {
    const table = {};
    for (const group of CONSOLE_STAGE_BOX_GROUPS) {
        for (const pattern of group.consoles) {
            for (const id of expand(pattern)) {
                const list = (table[id] ??= []);
                for (const box of group.boxes)
                    if (!list.includes(box))
                        list.push(box);
            }
        }
    }
    return table;
}
/** Console id -> catalogue keys of the stage boxes that work with it, in the order of `CONSOLE_STAGE_BOX_GROUPS`. */
export const CONSOLE_STAGE_BOXES = buildTable();
/** The stage boxes that work with the desk; `[]` for a desk the table says nothing about. */
export function compatibleStageBoxKeys(consoleId) {
    return CONSOLE_STAGE_BOXES[consoleId] ?? [];
}
/** The catalogue key of a stage-box preset; `null` for a generic preset (a class, not a product) or an unknown id. */
export function stageBoxCatalogKey(presetId) {
    return STANDARD_STAGE_BOX_MODELS.find((p) => p.id === presetId)?.catalogKey ?? null;
}
/** The desk's SUGGESTED boxes (`stageBoxIds`, S1's order) as catalogue keys; a preset with no catalogue row is skipped. */
export function consoleNativeStageBoxKeys(consoleId) {
    const row = CONSOLE_MODELS.find((c) => c.id === consoleId);
    if (!row)
        return [];
    return row.stageBoxIds.map(stageBoxCatalogKey).filter((k) => k !== null);
}
//# sourceMappingURL=console-stage-boxes.js.map